import { useCallback } from "react"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useDropzone } from "react-dropzone"
import type { FileRejection } from "react-dropzone"
import pluralize from "pluralize"
import { DropTarget } from "components/upload/components/drop-target"
import { SKILL_UPLOAD_CONFIG } from "components/upload/constants"

const INVALID_TYPE_MESSAGE = "is not a .zip or .skill file. Only .zip and .skill are accepted."
export const TOO_LARGE_MESSAGE = `exceeds the ${SKILL_UPLOAD_CONFIG.maxSize / (1024 * 1024)} MB limit for a single upload.`
const TOO_MANY_FILES_MESSAGE = "Only one skill package can be uploaded at a time."
const FORMAT_HINT = "You can only upload a .zip or .skill file."

const TARGET_SX = { minHeight: "26rem", cursor: "pointer" } satisfies SxProps<Theme>
const BROWSE_LINK_SX = { color: "primary.main", textDecoration: "underline" } satisfies SxProps<Theme>

function rejectionMessage({ file, errors }: FileRejection): string {
  const codes = errors.map((error) => error.code)
  if (codes.includes("file-invalid-type")) return `${file.name} ${INVALID_TYPE_MESSAGE}`
  if (codes.includes("file-too-large")) return `${file.name} ${TOO_LARGE_MESSAGE}`
  return `${file.name} ${TOO_MANY_FILES_MESSAGE}`
}

export interface SkillDropTargetProps {
  onFiles: (files: File[]) => void
  onReject: (message: string) => void
}

export function SkillDropTarget({ onFiles, onReject }: SkillDropTargetProps) {
  const onDrop = useCallback((accepted: File[], rejected: FileRejection[]) => {
    if (rejected.length > 0) onReject(`${rejected.length} ${pluralize("file", rejected.length)} will not be uploaded: ${rejected.map(rejectionMessage).join(", ")}`)
    if (accepted.length > 0) onFiles(accepted)
  }, [onFiles, onReject])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: SKILL_UPLOAD_CONFIG.acceptFormat,
    maxSize: SKILL_UPLOAD_CONFIG.maxSize,
    maxFiles: 1,
  })
  return (
    <DropTarget {...getRootProps()} isActive={isDragActive} sx={TARGET_SX}>
      <input {...getInputProps()} />
      <Typography variant="subtitle1" component="p">
        {isDragActive ? "Drop files here..." : <>Drag files here or <Box component="span" sx={BROWSE_LINK_SX}>browse</Box></>}
      </Typography>
      {!isDragActive && <Typography variant="body2" component="p">{FORMAT_HINT}</Typography>}
    </DropTarget>
  )
}
